import { useState } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahho5roh653ivfvnvt0';

const topics = ['内容纠错', '话题投稿', '合作咨询', '订阅每日热点', '其他'];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      form.reset();
      setStatus('success');
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value).trim();
      if (!text) return;
      params.append(key, text);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam|form data is spam/i.test(String(serverMsg));
      const isOk = response.ok && parsed?.code === 'OK';

      if (!isOk || isSpam) {
        setFormError(serverMsg || '提交失败，请稍后再试。');
        setStatus('error');
        return;
      }

      form.reset();
      setStatus('success');
    } catch {
      setFormError('网络异常，提交没有成功，请稍后再试。');
      setStatus('error');
    }
  };

  return (
    <form
      data-readdy-form
      id="about-contact-form"
      onSubmit={handleSubmit}
      className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8"
    >
      <h3 className="font-heading text-xl font-bold text-foreground-950">联系我们 / 订阅今日热点</h3>
      <p className="mt-2 text-sm text-foreground-600 leading-7">
        内容纠错、话题投稿、合作咨询都可以在这里留言；想每天收到一份热点清单，选择「订阅每日热点」即可。
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-2">
            邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label htmlFor="contact-nickname" className="block text-sm text-foreground-700 mb-2">
            称呼
          </label>
          <input
            id="contact-nickname"
            type="text"
            name="nickname"
            autoComplete="nickname"
            placeholder="怎么称呼你"
            className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="contact-topic" className="block text-sm text-foreground-700 mb-2">
          留言类型
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue={topics[0]}
          className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          {topics.map((topic) => (
            <option key={topic} value={topic}>
              {topic}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5">
        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-2">
          具体说明
        </label>
        <textarea
          id="contact-message"
          name="message"
          maxLength={500}
          rows={5}
          placeholder="请尽量写清楚话题名称、时间与可核对的公开来源，方便我们快速处理。"
          className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 resize-none"
        ></textarea>
        <p className="mt-2 text-xs text-foreground-400">最多 500 字。</p>
      </div>

      {/* 预留字段，用于拦截自动填表程序，请勿填写 */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-note-aux"
      />

      {status === 'success' && (
        <p className="mt-5 flex items-center gap-2 text-sm text-secondary-700 bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
          </span>
          已经收到，我们会在工作日尽快查看并回复。
        </p>
      )}

      {status === 'error' && (
        <p className="mt-5 flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-error-warning-line" aria-hidden="true"></i>
          </span>
          {formError || '提交失败，请稍后再试。'}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-6 w-full sm:w-auto px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {status === 'submitting' ? (
          <>
            <span className="w-4 h-4 flex items-center justify-center animate-spin">
              <i className="ri-loader-4-line" aria-hidden="true"></i>
            </span>
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" aria-hidden="true"></i>
            提交留言
          </>
        )}
      </button>
    </form>
  );
}